/**
 * The board's progress line. Leaving work on skill routing is normal, so the wording
 * reports what is assigned directly and what uses skill routing, rather than counting
 * towards assigning every schedule. Tasks given their own trade inside schedules that
 * otherwise use skill routing count too, so task-only assignments are never described as
 * everything using skill routing.
 *
 * @param {{ assignedSchedules: number, assignedTasks?: number, totalSchedules: number, assignedHours: number, totalHours: number }} progress
 * @returns {string}
 */
export function describeAssignmentProgress({
    assignedSchedules,
    assignedTasks = 0,
    totalSchedules,
    assignedHours,
    totalHours,
}) {
    if (!totalSchedules) return "This regime has no schedules to assign."
    if (!assignedSchedules && !assignedTasks) {
        return "No schedules assigned directly yet. Every schedule uses skill routing."
    }
    const share = describeShare(assignedHours, totalHours)
    const parts = [
        assignedSchedules && `${assignedSchedules} ${"schedule".pluralize(assignedSchedules)}`,
        assignedTasks && `${assignedTasks} ${"task".pluralize(assignedTasks)}`,
    ]
    const assigned = `${parts.filter(Boolean).join(" and ")} assigned directly, covering ${share} of hours.`
    // Assigned tasks sit in schedules that otherwise use skill routing, so there is always a rest
    if (assignedTasks) return `${assigned} Everything else uses skill routing.`
    const rest = totalSchedules - assignedSchedules
    if (!rest) return `${assigned} None use skill routing.`
    if (rest === 1) return `${assigned} The other schedule uses skill routing.`
    return `${assigned} The other ${rest} schedules use skill routing.`
}

// A little assigned work in a large regime rounds to 0%, which reads as if nothing counted
function describeShare(assignedHours, totalHours) {
    const percent = totalHours > 0 ? (assignedHours / totalHours) * 100 : 0
    if (percent > 0 && percent < 1) return "less than 1%"
    return `${Math.round(percent)}%`
}
